import React, { useEffect, useMemo, useState } from 'react';
import ArrowForward from '@mui/icons-material/ArrowForwardIos';
import ArrowBack from '@mui/icons-material/ArrowBack';
import { Radio, Tooltip } from '@mui/material';
import { useSnackbar } from 'material-ui-snackbar-provider';

import { getAllFolders, getFolderById, getShallowFolder, patchMoveFolders, patchMoveVideofeeds } from '../../../api';
import { ReactComponent as PurpleFolderIcon } from '../../../assets/icons/folder-purple-new.svg';
import { ReactComponent as ComposerIcon } from '../../../assets/icons/composer.svg';
import { ReactComponent as MovieIcon } from '../../../assets/icons/movieCollection.svg';
import { ReactComponent as TimelineIcon } from '../../../assets/icons/timeline.svg';
import { FOLDER_TYPE, PATHS, VIDEOFEED, VIDEOFEED_TYPE, VIDEOPAGE_TYPE } from '../../../enums';
import { SortOptions, SortType } from '../../../enums/SORT';
import { useAppSelector } from '../../../store/hooks';
import { ContainedButton, OutlinedButton } from '../../modules/Buttons';
import Modal from '../../modules/Modal';
import { beginMoveUndo, rememberFolderLocations } from '../../../helpers/moveUndo';
import styles from '../../VideofeedsList/MoveVideoFeedModal/MoveVideoFeedModal.module.scss';

type MoveFoldersModalProps = {
  open: boolean;
  folders: FOLDER_TYPE[];
  feeds?: VIDEOFEED[];
  sourceFolderId?: string | null;
  sourceAncestorIds?: string[];
  onClose: () => void;
  onMoved: () => void;
};

const EMPTY_SOURCE_ANCESTOR_IDS: string[] = [];

const formatUpdatedAt = (value?: string) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const isComposerFeed = (feed: VIDEOFEED) =>
  feed.type === VIDEOFEED_TYPE.COMPOSER ||
  feed.type === VIDEOFEED_TYPE.MY_COMPOSER;

const getFeedKindLabel = (feed: VIDEOFEED) =>
  isComposerFeed(feed) ? 'Online composer' : 'Movie collection';

const shouldMoveAsReference = (item: VIDEOFEED) =>
  Boolean((item as any).move_as_reference) ||
  (item.parent_videofeed_id !== null &&
    item.parent_videofeed_id !== undefined);

const isCollectionLikeFeed = (feed: VIDEOFEED) =>
  Boolean(feed?.is_collection_root) ||
  (!feed?.parent_videofeed_id && Number(feed?.movie_collection_id || 0) > 0) ||
  Boolean(feed?.has_reference_timelines) ||
  Boolean(feed?.reference_timelines?.length);

const getFeedMoveTitle = (feed: VIDEOFEED) =>
  !shouldMoveAsReference(feed) && isCollectionLikeFeed(feed)
    ? feed.collection_name || feed.collection_title || feed.title
    : feed.title;

export const buildMovedVideofeedUrl = (
  feed: VIDEOFEED,
  folderId: string,
  target: 'my_folders' | 'shared_with_me',
) => {
  const clipId = feed?.videos?.[0]?.id || 0;
  const parentPage =
    target === 'shared_with_me' ? PATHS.SHARED_WITH_ME.label : PATHS.MY_FOLDERS.label;

  switch (feed.type) {
    case VIDEOFEED_TYPE.COMPOSER:
      return `${PATHS.COMPOSER_PAGE.path}?feedId=${feed.id}&folderId=${folderId}&fileside=true&feed=0&clipId=${clipId}&clip=0&parentPage=${parentPage}&pageType=${VIDEOPAGE_TYPE.COMPOSER}${
        target === 'shared_with_me' ? '&shared=true' : ''
      }`;
    case VIDEOFEED_TYPE.MY_COMPOSER:
      return `${PATHS.COMPOSER_PAGE.path}?feedId=${feed.id}&fileside=true&feed=0&clipId=${clipId}&clip=0&parentPage=${parentPage}&pageType=${VIDEOPAGE_TYPE.MY_COMPOSER}${
        target === 'shared_with_me' ? '&sharedComposer=true' : ''
      }`;
    default:
      return `${PATHS.TIMELINE.path}?feedId=${feed.id}&folderId=${folderId}&fileside=true&feed=0&clipId=${clipId}&clip=0&parentPage=${parentPage}&pageType=${VIDEOPAGE_TYPE.TIMELINE}${
        target === 'shared_with_me' ? '&shared=true' : ''
      }`;
  }
};

const MoveFoldersModal: React.FC<MoveFoldersModalProps> = ({
  open,
  folders,
  feeds = [],
  sourceFolderId,
  sourceAncestorIds = EMPTY_SOURCE_ANCESTOR_IDS,
  onClose,
  onMoved,
}) => {
  const notification = useSnackbar();
  const { currentUser } = useAppSelector((state) => state.user);
  const [sourceTrail, setSourceTrail] = useState<FOLDER_TYPE[]>([]);
  const [browserTrail, setBrowserTrail] = useState<FOLDER_TYPE[]>([]);
  const [selectedDestinationTrail, setSelectedDestinationTrail] = useState<FOLDER_TYPE[]>([]);
  const [folderOptions, setFolderOptions] = useState<FOLDER_TYPE[]>([]);
  const [openedFolder, setOpenedFolder] = useState<FOLDER_TYPE | null>(null);
  const [selectedDestinationId, setSelectedDestinationId] = useState<string | null | undefined>();
  const [loading, setLoading] = useState(false);
  const rootLabel = 'My Folders';

  const selectedFolderIds = useMemo(
    () => folders.map((folder) => String(folder.id)),
    [folders],
  );
  const selectedFolderIdSet = useMemo(
    () => new Set(selectedFolderIds),
    [selectedFolderIds],
  );
  const totalItemsToMove = folders.length + feeds.length;
  const moveLabel =
    totalItemsToMove === 1 && folders.length === 1
      ? `Move "${folders[0].name}"`
      : totalItemsToMove === 1 && feeds.length === 1
        ? `Move "${getFeedMoveTitle(feeds[0])}"`
        : `Move ${totalItemsToMove} items`;

  const isMovableDestination = (folder: FOLDER_TYPE) =>
    (!folder.organization?.id ||
      String(folder.organization.id) === String(currentUser?.organization?.id)) &&
    !folder.is_collaborative;

  const canMoveFoldersToRoot = folders.every(
    (folder) => String(folder.owner?.id) === String(currentUser?.id),
  );

  const fetchFolders = async (parentFolderId?: string) => {
    const response = await getAllFolders({
      page: 0,
      limit: 1000,
      sort: SortType.ASC,
      type: VIDEOFEED_TYPE.TIMELINE,
      sortOption: SortOptions.NAME,
      parentFolderId,
    });

    return response.data.documents as FOLDER_TYPE[];
  };

  const getDestinationForTrail = (trail: FOLDER_TYPE[]) => {
    const currentFolder = trail[trail.length - 1];
    if (!currentFolder) {
      if (feeds.length) {
        return undefined;
      }

      return sourceFolderId && canMoveFoldersToRoot ? null : undefined;
    }

    return String(currentFolder.id) === String(sourceFolderId)
      ? undefined
      : String(currentFolder.id);
  };

  const openBrowserLevel = async (
    nextTrail: FOLDER_TYPE[],
    nextSelectedDestinationId: string | null | undefined = undefined,
    nextSelectedDestinationTrail: FOLDER_TYPE[] = selectedDestinationTrail,
  ) => {
    const nextParentFolderId = nextTrail[nextTrail.length - 1]?.id;
    const [nextFolders, openedFolderResponse] = await Promise.all([
      fetchFolders(nextParentFolderId),
      nextParentFolderId ? getFolderById(nextParentFolderId) : Promise.resolve(null),
    ]);
    setBrowserTrail(nextTrail);
    setFolderOptions(nextFolders);
    setOpenedFolder(openedFolderResponse?.data || null);
    setSelectedDestinationId(nextSelectedDestinationId);
    setSelectedDestinationTrail(nextSelectedDestinationTrail);
  };

  useEffect(() => {
    let active = true;

    const initializeModal = async () => {
      if (!open) return;

      setLoading(true);
      try {
        const pathIds = sourceFolderId
          ? [...sourceAncestorIds, sourceFolderId]
          : [];
        const responses = await Promise.all(
          pathIds.map((id) => getShallowFolder(id)),
        );
        const trail = responses.map((response) => response.data as FOLDER_TYPE);
        const [nextFolders, openedFolderResponse] = await Promise.all([
          fetchFolders(trail[trail.length - 1]?.id),
          trail[trail.length - 1]?.id
            ? getFolderById(trail[trail.length - 1].id)
            : Promise.resolve(null),
        ]);

        if (!active) return;
        setSourceTrail(trail);
        setBrowserTrail(trail);
        setFolderOptions(nextFolders);
        setOpenedFolder(openedFolderResponse?.data || null);
        setSelectedDestinationId(undefined);
        setSelectedDestinationTrail([]);
      } catch (err: any) {
        if (!active) return;
        notification.showMessage(
          err?.response?.data?.message || 'Failed to load folders',
        );
        onClose();
      } finally {
        if (active) setLoading(false);
      }
    };

    initializeModal();

    return () => {
      active = false;
    };
  }, [open, sourceFolderId, sourceAncestorIds, notification]);

  const onOpenFolder = async (folder: FOLDER_TYPE) => {
    setLoading(true);
    try {
      await openBrowserLevel([...browserTrail, folder]);
    } finally {
      setLoading(false);
    }
  };

  const onOpenAndSelectFolder = async (folder: FOLDER_TYPE) => {
    setLoading(true);
    try {
      const nextTrail = [...browserTrail, folder];
      await openBrowserLevel(nextTrail, String(folder.id), nextTrail);
    } finally {
      setLoading(false);
    }
  };

  const onBrowserBack = async () => {
    setLoading(true);
    try {
      const nextTrail = browserTrail.slice(0, -1);
      await openBrowserLevel(nextTrail);
    } finally {
      setLoading(false);
    }
  };

  const onBreadcrumbClick = async (index: number) => {
    setLoading(true);
    try {
      const nextTrail = index >= 0 ? browserTrail.slice(0, index + 1) : [];
      await openBrowserLevel(nextTrail);
    } finally {
      setLoading(false);
    }
  };

  const browsingFolder = browserTrail[browserTrail.length - 1] || null;
  const onSelectFolder = (folder: FOLDER_TYPE) => {
    setSelectedDestinationId(String(folder.id));
    setSelectedDestinationTrail([...browserTrail, folder]);
  };

  const onSelectCurrentFolder = () => {
    const nextDestination = getDestinationForTrail(browserTrail);
    setSelectedDestinationId(nextDestination);
    setSelectedDestinationTrail(nextDestination === undefined ? [] : browserTrail);
  };

  const moveFoldersRequest = async () => {
    if ((!selectedFolderIds.length && !feeds.length) || selectedDestinationId === undefined) {
      return;
    }

    const showMoveUndo = beginMoveUndo();
    const undoFolders = rememberFolderLocations(folders);
    const undoSteps: Array<() => Promise<unknown>> = [];
    let completed = false;
    setLoading(true);
    try {
      const destinationId = selectedDestinationId === null ? '' : selectedDestinationId;

      if (feeds.length) {
        if (!sourceFolderId || !destinationId) {
          notification.showMessage('Failed to move items');
          return;
        }

        const referenceItems = feeds.filter(shouldMoveAsReference);
        const regularItems = feeds.filter((item) => !shouldMoveAsReference(item));

        const referenceGroups = referenceItems.reduce<Record<string, VIDEOFEED[]>>(
          (acc, item) => {
            const key = String(item.movie_collection_id || item.parent_videofeed_id || item.id);
            acc[key] = [...(acc[key] || []), item];
            return acc;
          },
          {},
        );

        const moveFeeds = (fromId: string, toId: string) => patchMoveVideofeeds(fromId, toId, [
          ...regularItems.map((videofeed) => ({
            videofeedId: videofeed.id,
            urlPath: encodeURI(buildMovedVideofeedUrl(videofeed, toId, 'my_folders')),
            sharedUrlPath: encodeURI(buildMovedVideofeedUrl(videofeed, toId, 'shared_with_me')),
          })),
          ...Object.values(referenceGroups).map((group) => ({
            videofeedId: group[0].id,
            videofeedIds: group.map((item) => item.id),
            urlPath: encodeURI(buildMovedVideofeedUrl(group[0], toId, 'my_folders')),
            sharedUrlPath: encodeURI(buildMovedVideofeedUrl(group[0], toId, 'shared_with_me')),
          })),
        ]);
        await moveFeeds(sourceFolderId, destinationId);
        undoSteps.push(() => moveFeeds(destinationId, sourceFolderId));
      }

      if (selectedFolderIds.length) {
        const destinationUrlPath = encodeURI(
          selectedDestinationId
            ? `${PATHS.VIDEOFEEDS_LIST.path}?folderId=${selectedDestinationId}&parentPage=${PATHS.MY_FOLDERS.label}`
            : PATHS.MY_FOLDERS.path,
        );
        const sharedDestinationUrlPath = encodeURI(
          selectedDestinationId
            ? `${PATHS.SHARED_VIDEOFEEDS_LIST.path}?folderId=${selectedDestinationId}&parentPage=${PATHS.SHARED_WITH_ME.label}`
            : PATHS.SHARED_WITH_ME.path,
        );

        await patchMoveFolders(
          selectedFolderIds,
          selectedDestinationId,
          destinationUrlPath,
          sharedDestinationUrlPath,
        );
        undoSteps.push(undoFolders);
      }
      completed = true;
      onMoved();
      onClose();
    } catch (err: any) {
      notification.showMessage(
        err?.response?.data?.message || 'Failed to move items',
      );
    } finally {
      if (undoSteps.length) {
        const destinationName = selectedDestinationTrail.at(-1)?.name || rootLabel;
        showMoveUndo(completed ? `Moved to ${destinationName}` : `Some items moved to ${destinationName}`, async () => {
          for (const undo of [...undoSteps].reverse()) await undo();
        });
        if (!completed) onMoved();
      }
      setLoading(false);
    }
  };

  const destinationOptions = folderOptions.filter(
    (folder) =>
      !selectedFolderIdSet.has(String(folder.id)) &&
      isMovableDestination(folder),
  );
  const visibleDestinationFeeds = (openedFolder?.videofeeds || []).filter(
    (feed) => String(feed?.status || '').toUpperCase() !== 'ARCHIVED',
  );
  const sourcePath = [rootLabel, ...sourceTrail.map((folder) => folder.name)]
    .filter(Boolean)
    .join(' / ');
  const selectedDestinationPath =
    selectedDestinationId === null
      ? rootLabel
      : selectedDestinationId &&
        selectedDestinationTrail.length
        ? [rootLabel, ...selectedDestinationTrail.map((folder) => folder.name)]
            .filter(Boolean)
            .join(' / ')
        : '';
  const primaryFolder = folders[0];
  const primaryFeed = feeds[0];
  const summaryTitle =
    totalItemsToMove === 1 && folders.length === 1
      ? primaryFolder?.name || ''
      : totalItemsToMove === 1 && feeds.length === 1
        ? primaryFeed ? getFeedMoveTitle(primaryFeed) : ''
        : totalItemsToMove > 1
        ? `${totalItemsToMove} selected items`
        : '';
  const summaryMeta =
    totalItemsToMove === 1 && folders.length === 1
      ? primaryFolder?.parent_folder_id ? 'Subfolder' : 'Folder'
      : totalItemsToMove === 1 && feeds.length === 1
        ? primaryFeed
          ? getFeedKindLabel(primaryFeed)
          : ''
        : totalItemsToMove > 1
        ? `${folders.length} folder${folders.length === 1 ? '' : 's'} • ${feeds.length} videofeed${feeds.length === 1 ? '' : 's'}`
        : '';
  const selectedItemsPreview = useMemo(
    () =>
      feeds.map((feed) => {
        if (shouldMoveAsReference(feed)) {
          return {
            id: `reference-${feed.id}`,
            title: feed.title,
            meta: `Reference timeline${
              feed.collection_name || feed.collection_title
                ? ` • ${feed.collection_name || feed.collection_title}`
                : ''
            }`,
            icon: 'timeline' as const,
          };
        }

        if (isComposerFeed(feed)) {
          return {
            id: `composer-${feed.id}`,
            title: feed.title,
            meta: 'Online composer',
            icon: 'composer' as const,
          };
        }

        return {
          id: `feed-${feed.id}`,
          title: feed.collection_name || feed.title,
          meta: isCollectionLikeFeed(feed) ? 'Movie collection' : 'Videofeed',
          icon: 'movie' as const,
        };
      }),
    [feeds],
  );
  const selectedItemsTooltip = (
    <div className={styles.selectionPreview}>
      {folders.map((folder) => (
        <div className={styles.selectionPreviewRow} key={`folder-${folder.id}`}>
          <PurpleFolderIcon
            className={`${styles.selectionPreviewIcon} ${styles.selectionPreviewFolderIcon}`}
          />
          <div className={styles.selectionPreviewText}>
            <div className={styles.selectionPreviewLabel}>{folder.name}</div>
            <div className={styles.selectionPreviewMeta}>
              {folder.parent_folder_id ? 'Subfolder' : 'Folder'}
            </div>
          </div>
        </div>
      ))}

      {selectedItemsPreview.map((item) => (
        <div className={styles.selectionPreviewRow} key={item.id}>
          {item.icon === 'composer' ? (
            <ComposerIcon
              className={`${styles.selectionPreviewIcon} ${styles.selectionPreviewComposerIcon}`}
            />
          ) : item.icon === 'timeline' ? (
            <TimelineIcon
              className={`${styles.selectionPreviewIcon} ${styles.selectionPreviewTimelineIcon}`}
            />
          ) : (
            <MovieIcon
              className={`${styles.selectionPreviewIcon} ${styles.selectionPreviewMovieIcon}`}
            />
          )}
          <div className={styles.selectionPreviewText}>
            <div className={styles.selectionPreviewLabel}>{item.title}</div>
            <div className={styles.selectionPreviewMeta}>{item.meta}</div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <Modal open={open} onClose={onClose} loading={loading}>
      <div className={styles.modalBody}>
        {!!totalItemsToMove && (
          <>
            <div className={styles.modalTitle}>{moveLabel}</div>
            <div className={styles.modalSubtitle}>
              Choose a new location for this content.
            </div>

            <div className={styles.summaryGrid}>
              <div className={styles.infoCard}>
                <div className={styles.infoLabel}>Moving</div>
                <Tooltip
                  title={selectedItemsTooltip}
                  placement="bottom-start"
                  arrow
                  PopperProps={{
                    sx: {
                      '& .MuiTooltip-tooltip': {
                        backgroundColor: '#4a4a4f !important',
                        background: '#4a4a4f !important',
                        border: '1px solid #5a5a61',
                        boxShadow: '0 18px 44px rgba(0, 0, 0, 0.34)',
                        borderRadius: 'var(--ui-border-radius)',
                        maxWidth: 360,
                        padding: '0 !important',
                        opacity: '1 !important',
                        filter: 'none !important',
                        backdropFilter: 'none !important',
                        WebkitBackdropFilter: 'none !important',
                        backgroundImage: 'none !important',
                      },
                      '& .MuiTooltip-arrow': {
                        color: '#4a4a4f !important',
                      },
                      '& .MuiTooltip-tooltip *': {
                        opacity: '1 !important',
                      },
                    },
                  }}
                >
                  <div className={styles.infoContent}>
                    <div className={styles.infoIconWrap}>
                      <PurpleFolderIcon className={styles.summaryIcon} />
                    </div>
                    <div className={styles.infoText}>
                      <div className={styles.infoTitleText}>{summaryTitle}</div>
                      <div className={styles.infoMetaText}>{summaryMeta}</div>
                    </div>
                  </div>
                </Tooltip>
              </div>

              <div className={styles.infoCard}>
                <div className={styles.infoLabel}>Current location</div>
                <div className={styles.infoContent}>
                  <div className={styles.infoIconWrap}>
                    <PurpleFolderIcon className={styles.summaryIcon} />
                  </div>
                    <div className={styles.infoText}>
                      <div className={styles.infoTitleText}>
                        {sourceTrail[sourceTrail.length - 1]?.name || rootLabel}
                      </div>
                      <div className={styles.infoMetaText}>{sourcePath}</div>
                    </div>
                  </div>
              </div>
            </div>

            <div className={styles.browserShell}>
              <div className={styles.browserToolbar}>
                <button
                  type="button"
                  className={styles.backButton}
                  onClick={onBrowserBack}
                  disabled={!browserTrail.length}
                >
                  <ArrowBack />
                </button>
                <div className={styles.browserPath}>
                  <button
                    type="button"
                    className={styles.browserPathButton}
                    onClick={() => onBreadcrumbClick(-1)}
                  >
                    {rootLabel}
                  </button>
                  {browserTrail.map((folder, index) => (
                    <React.Fragment key={folder.id}>
                      <span className={styles.browserPathSeparator}>/</span>
                      <button
                        type="button"
                        className={styles.browserPathButton}
                        onClick={() => onBreadcrumbClick(index)}
                      >
                        {folder.name}
                      </button>
                    </React.Fragment>
                  ))}
                </div>
                <button
                  type="button"
                  className={styles.currentFolderButton}
                  onClick={onSelectCurrentFolder}
                  disabled={getDestinationForTrail(browserTrail) === undefined}
                >
                  {selectedDestinationId === getDestinationForTrail(browserTrail)
                    ? 'Current folder selected'
                    : browsingFolder
                      ? 'Select this folder'
                      : 'Select My Folders'}
                </button>
              </div>

              <div className={styles.destinationBar}>
                <div className={styles.destinationHeading}>Selected destination</div>
                <div className={styles.destinationValue}>
                  {selectedDestinationId !== undefined
                    ? selectedDestinationPath
                    : 'Choose a folder from the list below'}
                </div>
                <div className={styles.selectionHint}>
                  Click a row to open and select it. Use the circle to select only.
                </div>
              </div>

              <div className={styles.listShell}>
                <div className={styles.listHeader}>
                  <span>Name</span>
                  <span>Type</span>
                  <span>Updated</span>
                  <span></span>
                </div>

                <div className={styles.folderList}>
                  {!destinationOptions.length && !visibleDestinationFeeds.length && (
                    <div className={styles.emptyState}>No items found</div>
                  )}

                  {destinationOptions.map((folder) => {
                    const isSelected = selectedDestinationId === String(folder.id);

                    return (
                      <div
                        className={`${styles.listRow} ${styles.folderRow} ${
                          isSelected ? styles.folderSelected : ''
                        }`}
                        key={folder.id}
                        onClick={() => onOpenAndSelectFolder(folder)}
                      >
                        <div className={styles.rowMain}>
                          <Radio
                            checked={isSelected}
                            onClick={(event) => event.stopPropagation()}
                            onChange={() => onSelectFolder(folder)}
                            sx={{
                              color: 'white',
                              '&, &.Mui-checked': {
                                color: '#39b3ea',
                              },
                            }}
                            size="small"
                          />
                          <PurpleFolderIcon className={styles.rowIcon} />
                          <div className={styles.rowTextWrap}>
                            <div className={styles.rowPrimary}>{folder.name}</div>
                            <div className={styles.rowSecondary}>
                              {folder.parent_folder_id ? 'Subfolder' : 'Folder'}
                            </div>
                          </div>
                        </div>
                        <div className={styles.rowType}>
                          {folder.parent_folder_id ? 'Subfolder' : 'Folder'}
                        </div>
                        <div className={styles.rowMeta}>{formatUpdatedAt(folder.updated_at)}</div>
                        <button
                          type="button"
                          className={styles.navigateButton}
                          onClick={(event) => {
                            event.stopPropagation();
                            onOpenFolder(folder);
                          }}
                        >
                          <ArrowForward sx={{ fontSize: '12px' }} />
                        </button>
                      </div>
                    );
                  })}

                  {visibleDestinationFeeds.map((feed) => (
                    <div className={`${styles.listRow} ${styles.disabledRow}`} key={`feed-${feed.id}`}>
                      <div className={styles.rowMain}>
                        <div className={styles.radioSpacer} />
                        {isComposerFeed(feed) ? (
                          <ComposerIcon className={styles.rowIcon} />
                        ) : (
                          <MovieIcon className={styles.rowIcon} />
                        )}
                        <div className={styles.rowTextWrap}>
                          <div className={styles.rowPrimary}>{feed.title}</div>
                          <div className={styles.rowSecondary}>Not selectable</div>
                        </div>
                      </div>
                      <div className={styles.rowType}>{getFeedKindLabel(feed)}</div>
                      <div className={styles.rowMeta}>{formatUpdatedAt(feed.updated_at)}</div>
                      <div className={styles.rowMetaMuted}>—</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className={styles.footerNote}>
              {selectedDestinationId !== undefined
                ? `Move destination: ${selectedDestinationPath}`
                : 'Select a folder to move this content into.'}
            </div>

            <div className={styles.actionBtns}>
              <OutlinedButton kind="default" disabled={loading} onClick={onClose}>
                Cancel
              </OutlinedButton>
              <ContainedButton
                onClick={moveFoldersRequest}
                disabled={selectedDestinationId === undefined || loading}
              >
                Move here
              </ContainedButton>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default MoveFoldersModal;
